import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LakshLogo } from '@/components/LakshLogo';
import { toast } from 'sonner';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import axios from 'axios';
import {
  Users,
  IndianRupee,
  FileText,
  Calendar,
  TrendingUp,
  AlertTriangle,
  LogOut,
  LayoutDashboard,
  Briefcase,
  PieChart,
  UserPlus,
  ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (num) => {
  if (num >= 10000000) return `₹${(num/10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num/100000).toFixed(2)} L`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/ca/dashboard' },
  { icon: Users, label: 'Clients', path: '/ca/clients' },
  { icon: Calendar, label: 'Compliance', path: '/ca/compliance' },
  { icon: IndianRupee, label: 'Revenue', path: '/ca/revenue' },
];

export default function CADashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firm, setFirm] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('laksh_ca_token');
    const userData = localStorage.getItem('laksh_ca_user');
    const firmData = localStorage.getItem('laksh_ca_firm');
    
    if (!token) {
      navigate('/ca/login');
      return;
    }
    
    if (userData) setUser(JSON.parse(userData));
    if (firmData) setFirm(JSON.parse(firmData));
    
    fetchDashboard(token);
  }, [navigate]);

  const fetchDashboard = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ca/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Dashboard error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('laksh_ca_token');
        navigate('/ca/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('laksh_ca_token');
    localStorage.removeItem('laksh_ca_user');
    localStorage.removeItem('laksh_ca_firm');
    navigate('/ca/login');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06100A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D9E75] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#06100A]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0a1a12] border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <LakshLogo height={32} />
          <div className="mt-2 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#1D9E75]" />
            <span className="text-xs text-[#1D9E75] font-medium">CA Portal</span>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              data-testid={`ca-nav-${item.label.toLowerCase()}`}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-gray-400 transition-colors hover:bg-white/5 hover:text-white ${
                window.location.pathname === item.path ? 'bg-[#1D9E75]/20 text-[#1D9E75]' : ''
              }`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#1D9E75]/20 rounded-full flex items-center justify-center">
              <span className="text-[#1D9E75] font-semibold">
                {user?.name?.charAt(0) || 'C'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{firm?.name}</p>
            </div>
          </div>
          <FeedbackDialog portal="ca" token={localStorage.getItem('laksh_ca_token')} triggerClassName="w-full px-4 py-2 mb-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5" label="Send feedback" />
          <Button data-testid="ca-logout-btn" variant="ghost" onClick={handleLogout} className="w-full text-gray-400 hover:text-white hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white font-['Outfit']">
                Welcome back, {user?.name?.split(' ')[0] || 'Partner'}
              </h1>
              <p className="text-gray-400">{firm?.name}</p>
            </div>
            <Link to="/ca/clients" data-testid="ca-dashboard-invite-link">
              <Button className="bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white gap-2">
                <UserPlus className="w-4 h-4" /> Invite Client
              </Button>
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Total Clients</p>
                    <p className="text-3xl font-bold text-white font-mono">{stats?.total_clients || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">{stats?.active_clients || 0} active</p>
                  </div>
                  <div className="w-12 h-12 bg-[#1D9E75]/20 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#1D9E75]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Revenue Share</p>
                    <p className="text-3xl font-bold text-[#EF9F27] font-mono">{formatCurrency(stats?.revenue_share_earned || 0)}</p>
                    <p className="text-xs text-gray-500 mt-1">{stats?.revenue_share_pct || 20}% of client MRR</p>
                  </div>
                  <div className="w-12 h-12 bg-[#EF9F27]/20 rounded-lg flex items-center justify-center">
                    <IndianRupee className="w-6 h-6 text-[#EF9F27]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Client Revenue</p>
                    <p className="text-3xl font-bold text-white font-mono">{formatCurrency(stats?.total_client_revenue || 0)}</p>
                    <p className="text-xs text-gray-500 mt-1">Total paid invoices</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a1a12] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Outstanding</p>
                    <p className="text-3xl font-bold text-white font-mono">{formatCurrency(stats?.total_outstanding || 0)}</p>
                    <p className="text-xs text-gray-500 mt-1">Across all clients</p>
                  </div>
                  <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-red-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Compliance Alerts & Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Compliance */}
            <Card className="bg-[#0a1a12] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#EF9F27]" />
                  Upcoming Compliance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-lg">
                    <div>
                      <p className="font-medium text-white">GSTR-1 Due</p>
                      <p className="text-sm text-gray-400">{stats?.upcoming_gstr1_count || 0} clients pending</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-[#EF9F27]">{stats?.next_gstr1_due || '-'}</p>
                      <p className="text-xs text-gray-500">11th of month</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-[#1D9E75]/10 border border-[#1D9E75]/30 rounded-lg">
                    <div>
                      <p className="font-medium text-white">GSTR-3B Due</p>
                      <p className="text-sm text-gray-400">{stats?.upcoming_gstr3b_count || 0} clients pending</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-[#1D9E75]">{stats?.next_gstr3b_due || '-'}</p>
                      <p className="text-xs text-gray-500">20th of month</p>
                    </div>
                  </div>
                </div>
                
                <Link to="/ca/compliance" data-testid="ca-dashboard-calendar-link">
                  <Button variant="ghost" className="w-full mt-4 text-gray-400 hover:text-white">
                    View Full Calendar <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-[#0a1a12] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#1D9E75]" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/ca/clients" className="block" data-testid="ca-quick-clients">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-[#1D9E75]" />
                      <span className="text-white">View All Clients</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </div>
                </Link>
                
                <Link to="/ca/compliance" className="block" data-testid="ca-quick-compliance">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#EF9F27]" />
                      <span className="text-white">Compliance Calendar</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </div>
                </Link>
                
                <Link to="/ca/revenue" className="block" data-testid="ca-quick-revenue">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <IndianRupee className="w-5 h-5 text-[#EF9F27]" />
                      <span className="text-white">Revenue Dashboard</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </div>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
